import PropTypes from 'prop-types';
import { Button, Icon } from '@evertrue/et-components';

/**
 * Step 3 of the bulk message creation wizard: Schedule & Launch.
 * Renders a summary of the bulk message, a test send control, and delivery schedule options.
 */
const BulkMessageScheduleStep = ({
  messageName,
  selectionMethod,
  selectedList,
  selectedSavedSearch,
  selectedContacts,
  successMetric,
  interactionTypes,
  portfolioTeams,
  subject,
  sharedUsers,
  testRecipient,
  setTestRecipient,
  testContactSearch,
  handleSendTest,
  scheduleType,
  setScheduleType,
  scheduleDate,
  setScheduleDate,
  scheduleTime,
  setScheduleTime,
}) => (
  <div className="bulk-message-schedule-step">
    <div className="bulk-message-schedule-step--intro">
      <h2 className="bulk-message-schedule-step--title">Finalize Your Bulk Message</h2>
      <p className="bulk-message-schedule-step--subtitle">Review your bulk message summary and choose when to send.</p>
    </div>

    {/* Bulk Message Summary Card */}
    <div className="bulk-message-schedule-step--card">
      <div className="bulk-message-schedule-step--card-header">
        <span>Bulk Message Summary</span>
      </div>
      <div className="bulk-message-schedule-step--card-body">
        <div className="bulk-message-schedule-step--summary">
          <div className="bulk-message-schedule-step--summary-row">
            <span className="bulk-message-schedule-step--summary-label">Bulk Message Name</span>
            <span className="bulk-message-schedule-step--summary-value">{messageName}</span>
          </div>
          <div className="bulk-message-schedule-step--summary-row">
            <span className="bulk-message-schedule-step--summary-label">Recipients</span>
            <span className="bulk-message-schedule-step--summary-value">
              {selectionMethod?.value === 'list' && selectedList && (
                <>
                  {selectedList.label}
                  {selectedList.count > 0 && (
                    <span className="bulk-message-schedule-step--summary-badge">
                      {selectedList.count.toLocaleString()} recipients
                    </span>
                  )}
                </>
              )}
              {selectionMethod?.value === 'search' && selectedSavedSearch && (
                <>
                  {selectedSavedSearch.label}
                  {selectedSavedSearch.count > 0 && (
                    <span className="bulk-message-schedule-step--summary-badge">
                      {selectedSavedSearch.count.toLocaleString()} recipients
                    </span>
                  )}
                </>
              )}
              {selectionMethod?.value === 'contacts' && (
                <>
                  {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
                  <span className="bulk-message-schedule-step--summary-badge">Manual Selection</span>
                </>
              )}
              {!selectedList && !selectedSavedSearch && selectionMethod?.value !== 'contacts' && (
                <span>Not selected</span>
              )}
            </span>
          </div>
          <div className="bulk-message-schedule-step--summary-row">
            <span className="bulk-message-schedule-step--summary-label">Success Metric</span>
            <span className="bulk-message-schedule-step--summary-value">
              {!successMetric?.type
                ? 'Not set'
                : (() => {
                    const pct = successMetric.targetPercentage || 0;
                    const cfg = successMetric.config || {};
                    switch (successMetric.type) {
                      case 'email_opened':
                        return `If ${pct}% of constituents open at least one email, this cadence is successful.`;
                      case 'email_replied':
                        return `If ${pct}% of constituents reply to at least one email, this cadence is successful.`;
                      case 'email_clicked':
                        return `If ${pct}% of constituents click a link in at least one email, this cadence is successful.`;
                      case 'interaction_created': {
                        const match = interactionTypes.find((t) => t.value === cfg.interactionType);
                        const typeName = match?.label || cfg.interactionType || 'any type';
                        return `If ${pct}% of constituents have an interaction of ${typeName} logged during the cadence, this cadence is successful.`;
                      }
                      case 'gift_given': {
                        const amt = cfg.minAmount
                          ? `$${Number(String(cfg.minAmount).replace(/,/g, '')).toLocaleString()}`
                          : 'any amount';
                        const dest = cfg.designation || 'any designation';
                        return `If ${pct}% of constituents make a gift of ${amt} or more to ${dest}, this cadence is successful.`;
                      }
                      case 'portfolio_stage_reached': {
                        const team = (portfolioTeams?.teams || []).find((t) => String(t.id) === String(cfg.teamId));
                        const teamName = team?.name || cfg.teamName || 'a team';
                        const stageName = cfg.stageName || 'a stage';
                        return `If ${pct}% of constituents assigned to ${teamName} reach the ${stageName} stage, this cadence is successful.`;
                      }
                      case 'saved_search': {
                        const searchName = cfg.savedSearchName || 'a saved search';
                        return `If ${pct}% of constituents qualify for "${searchName}", this cadence is successful.`;
                      }
                      case 'gifts_amount': {
                        const threshold = cfg.threshold ? `$${Number(cfg.threshold).toLocaleString()}` : '$0';
                        return `If ${pct}% of constituents give a total of ${threshold} or more, this cadence is successful.`;
                      }
                      case 'gifts_count':
                        return `If ${pct}% of constituents make ${cfg.threshold || 0} or more gifts, this cadence is successful.`;
                      case 'meetings_count':
                        return `If ${pct}% of constituents have ${cfg.threshold || 0} or more meetings, this cadence is successful.`;
                      case 'proposal_funded':
                        return `If ${pct}% of constituents have a proposal funded, this cadence is successful.`;
                      default:
                        return successMetric.type;
                    }
                  })()}
            </span>
          </div>
          <div className="bulk-message-schedule-step--summary-row">
            <span className="bulk-message-schedule-step--summary-label">Subject Line</span>
            <span className="bulk-message-schedule-step--summary-value">{subject || 'Not set'}</span>
          </div>
          <div className="bulk-message-schedule-step--summary-row">
            <span className="bulk-message-schedule-step--summary-label">Sharing</span>
            <span className="bulk-message-schedule-step--summary-value">
              {sharedUsers.length > 0
                ? `${sharedUsers.length} ${sharedUsers.length === 1 ? 'person' : 'people'}`
                : 'Restricted'}
            </span>
          </div>
        </div>
      </div>
    </div>

    {/* Test Send Card */}
    <div className="bulk-message-schedule-step--card">
      <div className="bulk-message-schedule-step--card-header">
        <Icon icon="send" className="bulk-message-schedule-step--card-icon" />
        <span>Test Send</span>
      </div>
      <div className="bulk-message-schedule-step--card-body">
        <p className="bulk-message-schedule-step--description">
          Send yourself a test message personalized for a specific recipient to preview how it will appear.
        </p>
        <div className="bulk-message-schedule-step--test-row">
          {testRecipient ? (
            <div className="bulk-message-schedule-step--selected-recipient">
              <span>{testRecipient.name}</span>
              {testRecipient.email && (
                <span className="bulk-message-schedule-step--recipient-email">{testRecipient.email}</span>
              )}
              <button
                type="button"
                className="bulk-message-schedule-step--clear-btn"
                onClick={() => setTestRecipient(null)}
              >
                <Icon icon="close" />
              </button>
            </div>
          ) : (
            <div className="bulk-message-schedule-step--search">
              <Icon icon="search" />
              <input
                type="text"
                placeholder="Search for a recipient to spoof..."
                value={testContactSearch.query}
                onChange={(e) => testContactSearch.setQuery(e.target.value)}
              />
              {testContactSearch.loading && <Icon icon="refresh" className="bulk-message-schedule-step--spinner" />}
            </div>
          )}
          <Button type="secondary" onClick={handleSendTest} disabled={!testRecipient}>
            Send Test
          </Button>
        </div>
        {!testRecipient && testContactSearch.results.length > 0 && (
          <div className="bulk-message-schedule-step--contact-results">
            {testContactSearch.results.map((contact) => {
              const contactAttrs = contact.properties || {};
              const name =
                contact.name ||
                contactAttrs.name_full?.value ||
                [contactAttrs.name_first?.value, contactAttrs.name_last?.value].filter(Boolean).join(' ') ||
                'Unknown';
              const firstEmail = contact.emails?.[0];
              const email = firstEmail?.email?.value || firstEmail?.email || '';
              return (
                <button
                  key={contact.id}
                  type="button"
                  className="bulk-message-schedule-step--contact-result"
                  onClick={() => {
                    setTestRecipient({ id: contact.id, name, email });
                    testContactSearch.setQuery('');
                  }}
                >
                  <span className="bulk-message-schedule-step--contact-name">{name}</span>
                  {email && <span className="bulk-message-schedule-step--contact-email">{email}</span>}
                </button>
              );
            })}
          </div>
        )}
        {!testRecipient && testContactSearch.query.length > 0 && testContactSearch.query.length < 2 && (
          <p className="bulk-message-schedule-step--hint">Type at least 2 characters to search.</p>
        )}
        <div className="bulk-message-schedule-step--info">
          <Icon icon="info" />
          <span>
            The test will be sent to your primary email address with all personalization tags replaced with the data
            from the selected recipient.
          </span>
        </div>
      </div>
    </div>

    {/* Delivery Schedule Card */}
    <div className="bulk-message-schedule-step--card">
      <div className="bulk-message-schedule-step--card-header">
        <Icon icon="event" className="bulk-message-schedule-step--card-icon" />
        <span>Delivery Schedule</span>
        <span className="bulk-message-schedule-step--timezone">
          {Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ')}
        </span>
      </div>
      <div className="bulk-message-schedule-step--card-body">
        <p className="bulk-message-schedule-step--tz-note">All times are shown in your current timezone.</p>
        <div className="bulk-message-schedule-step--options">
          <label className={`bulk-message-schedule-step--option ${scheduleType === 'now' ? 'is-selected' : ''}`}>
            <input
              type="radio"
              name="scheduleType"
              checked={scheduleType === 'now'}
              onChange={() => setScheduleType('now')}
            />
            <div className="bulk-message-schedule-step--option-content">
              <strong>Send Immediately</strong>
              <span>Your message will enter the queue as soon as you confirm.</span>
              {scheduleType === 'now' && (
                <div className="bulk-message-schedule-step--estimated">
                  <Icon icon="access-time" />
                  <span>Estimated delivery: Within 15 minutes</span>
                </div>
              )}
            </div>
          </label>
          <label className={`bulk-message-schedule-step--option ${scheduleType === 'later' ? 'is-selected' : ''}`}>
            <input
              type="radio"
              name="scheduleType"
              checked={scheduleType === 'later'}
              onChange={() => setScheduleType('later')}
            />
            <div className="bulk-message-schedule-step--option-content">
              <strong>Schedule for Later</strong>
              <span>Choose a specific date and time to send your message.</span>
              {scheduleType === 'later' && (
                <div className="bulk-message-schedule-step--datetime">
                  <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
                  <input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
                </div>
              )}
            </div>
          </label>
        </div>
      </div>
    </div>
  </div>
);

BulkMessageScheduleStep.propTypes = {
  messageName: PropTypes.string.isRequired,
  selectionMethod: PropTypes.object,
  selectedList: PropTypes.object,
  selectedSavedSearch: PropTypes.object,
  selectedContacts: PropTypes.array.isRequired,
  successMetric: PropTypes.object,
  interactionTypes: PropTypes.array,
  portfolioTeams: PropTypes.object,
  subject: PropTypes.string.isRequired,
  sharedUsers: PropTypes.array.isRequired,
  testRecipient: PropTypes.object,
  setTestRecipient: PropTypes.func.isRequired,
  testContactSearch: PropTypes.shape({
    query: PropTypes.string,
    setQuery: PropTypes.func,
    results: PropTypes.array,
    loading: PropTypes.bool,
  }).isRequired,
  handleSendTest: PropTypes.func.isRequired,
  scheduleType: PropTypes.string.isRequired,
  setScheduleType: PropTypes.func.isRequired,
  scheduleDate: PropTypes.string.isRequired,
  setScheduleDate: PropTypes.func.isRequired,
  scheduleTime: PropTypes.string.isRequired,
  setScheduleTime: PropTypes.func.isRequired,
};

export default BulkMessageScheduleStep;
